// @ts-nocheck
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Chart, Loader } from 'sonata-react-components';

import { getReportingProgressStats } from '@/store/selectors/reporting.selectors';
import { getChartTimeStep } from '@/store/selectors/reportingAdvancedFilter.selectors';

type changeLogPlotLine = {
  className: string;
  color: string;
  dashStyle: string;
  events: {
    click: () => void;
  };
  value: number;
  width: number;
};

type Props = {
  changeLogPlotLines: changeLogPlotLine[];
  chart: Record<string, any>;
  defaultFormatter: CallableFunction;
  getSeriesData: CallableFunction;
  selectedChart: string;
  xCategories: string[];
};

function ReportingChart({
  changeLogPlotLines,
  chart,
  defaultFormatter,
  getSeriesData,
  selectedChart,
  xCategories
}: Props) {
  const intl = useIntl();

  // @ts-ignore
  const stats = useSelector(store => getReportingProgressStats(store, selectedChart));
  const chartTimeStep = useSelector(getChartTimeStep);

  const getAdditionalYAxis = useMemo(() => {
    const yAxis = [];
    chart.yAxis && yAxis.push(...chart.yAxis);
    return yAxis;
  }, [chart]);

  const selectedSeries = useMemo(() => {
    if (!stats) {
      return [];
    }
    const principalSelectedChartStatData = getSeriesData(chart.stats[0].statKey);
    const avg =
      principalSelectedChartStatData.reduce((i: number, acc: number) => acc + i, 0) /
      principalSelectedChartStatData.length;
    const series = [
      ...chart.stats.map((statConfig: Record<string, any>) => ({
        animation: false,
        color: '#ED9D5B',
        dataLabels: { enabled: false },
        marker: {
          lineColor: '#ED9D5B'
        },
        name: intl.formatMessage({ id: `reporting.metrics.${statConfig.statKey}` }),
        pointPadding: 0.3,
        ...statConfig.seriesConfig,
        data: getSeriesData(statConfig.statKey).map(value => value ?? 0)
      }))
    ];
    series.push({
      animation: false,
      color: '#F774A0',
      data: principalSelectedChartStatData.map(() => avg),
      dataLabels: { enabled: false },
      lineWidth: 1,
      marker: {
        enabled: false
      },
      name: intl.formatMessage({ id: 'reporting.reportingCharts.avg' }),
      pointPadding: 0.3,
      tooltip: { className: 'medianTooltip' },
      type: 'line'
    });
    let prevPrevVal = 0;
    let prevVal = 0;
    let curVal = 0;
    const serie: number[] = [];
    principalSelectedChartStatData.forEach((currentValue: number, i: number) => {
      if (i === 0) {
        prevPrevVal = currentValue;
        prevVal = currentValue;
        curVal = currentValue;
      } else if (i === 1) {
        prevPrevVal = prevVal;
        prevVal = curVal;
        curVal = (prevVal + currentValue) / 2;
      } else {
        prevPrevVal = prevVal;
        prevVal = curVal;
        curVal = (prevPrevVal + prevVal + currentValue) / 3;
      }
      serie.push(curVal);
    });
    series.push({
      animation: false,
      color: '#7EAAE5',
      data: serie,
      dataLabels: { enabled: false },
      lineWidth: 1,
      marker: {
        enabled: false
      },
      name: intl.formatMessage({
        defaultMessage: 'Trend',
        id: 'reporting.reportingCharts.trend'
      }),
      pointPadding: 0.3,
      tooltip: { className: 'medianTooltip' },
      type: 'spline'
    });
    return series;
  }, [chart, getSeriesData, stats]);

  return (
    <div className="chartContainer">
      {!stats && (
        <div className="spinnerChartContainer">
          <Loader type="landing" variant="default" />
        </div>
      )}
      <Chart
        chart={{
          backgroundColor: '#fff',
          type: 'area'
        }}
        id="reportingChart"
        legend={{
          itemHiddenStyle: {
            color: '#ccc'
          },
          itemHoverStyle: {
            color: '#ED9D5B'
          },
          itemStyle: {
            color: '#777'
          },
          navigation: {
            activeColor: '#333',
            style: {
              color: '#333'
            }
          },
          reversed: true
        }}
        plotOptions={{
          area: {
            fillOpacity: 0.1
          },
          bar: {},
          series: {
            groupPadding: 0,
            lineWidth: 2,
            marker: {
              enabledThreshold: 0,
              fillColor: '#fff',
              lineWidth: 1
            },
            pointPadding: 0,
            pointWidth: 15
          }
        }}
        series={selectedSeries}
        title={{ text: '' }}
        tooltip={{
          formatter: defaultFormatter
        }}
        xAxis={[
          {
            categories: xCategories,
            labels: {
              rotation: 0,
              style: {
                color: '#ccc',
                fontFamily: 'Verdana, sans-serif',
                fontSize: '10px'
              }
            },
            plotLines: changeLogPlotLines,
            tickColor: '#ccc',
            tickWidth: 1,
            title: {
              style: {
                color: '#B2B9BE',
                fontFamily: 'OpenSansLight',
                fontSize: '16px'
              },
              text:
                selectedChart === 'dwellTimeChart'
                  ? intl.formatMessage({ id: 'reporting.reportingCharts.timeSections' })
                  : intl.formatMessage({ id: 'reporting.reportingCharts.timeStep' }, { timeStep: chartTimeStep })
            },
            type: 'category'
          }
        ]}
        yAxis={[
          {
            labels: {
              style: {
                color: chart.color,
                fontFamily: 'Verdana, sans-serif',
                fontSize: '10px'
              }
            },
            min: 0,
            tickWidth: 0,
            title: { text: null }
          },
          ...getAdditionalYAxis
        ]}
      />
    </div>
  );
}

export default ReportingChart;
